import { useState } from 'react';
import { ChevronRight, Check, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * StepByStep component - Guided exploration through sequential steps
 * Data structure: { steps: [{step_number, title, description, interaction_type, interaction_data}], completion_message }
 */
export default function StepByStep({ simulationData, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [stepInteractions, setStepInteractions] = useState({}); // Track interactions per step

  const steps = simulationData.steps || [];
  const completionMessage = simulationData.completion_message || 'Great job! You\'ve completed the exploration!';
  const currentStepData = steps[currentStep];

  const handleStepInteraction = (stepIndex) => {
    const newCompleted = new Set(completedSteps);
    newCompleted.add(stepIndex);
    setCompletedSteps(newCompleted);
    
    // Mark interaction as done
    setStepInteractions(prev => ({ ...prev, [stepIndex]: true }));
    
    // Auto-advance to next step after a short delay
    if (stepIndex < steps.length - 1) {
      setTimeout(() => {
        setCurrentStep(stepIndex + 1);
      }, 1500);
    } else {
      // Last step completed
      setTimeout(() => {
        if (onComplete) onComplete();
      }, 1000);
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!currentStepData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No steps available</p>
      </div>
    );
  }

  const isCurrentStepCompleted = completedSteps.has(currentStep);
  const allStepsCompleted = completedSteps.size >= steps.length;

  return (
    <div className="space-y-6">
      {/* Progress indicator */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {steps.map((step, idx) => (
            <div key={idx} className="flex items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                  idx < currentStep || completedSteps.has(idx)
                    ? 'bg-green-500 text-white'
                    : idx === currentStep
                    ? 'bg-teal-500 text-white ring-2 ring-teal-300'
                    : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                }`}
              >
                {completedSteps.has(idx) ? (
                  <Check className="w-4 h-4" />
                ) : (
                  idx + 1
                )}
              </div>
              {idx < steps.length - 1 && (
                <ChevronRight className={`w-4 h-4 mx-1 ${
                  idx < currentStep ? 'text-green-500' : 'text-gray-300'
                }`} />
              )}
            </div>
          ))}
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Step {currentStep + 1} of {steps.length}
        </div>
      </div>

      {/* Current step content */}
      <div className="bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 border-2 border-teal-300 dark:border-teal-700 rounded-lg p-6">
        <div className="mb-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {currentStepData.title}
          </h3>
          <p className="text-gray-700 dark:text-gray-300">
            {currentStepData.description}
          </p>
        </div>

        {/* Step interaction */}
        {!isCurrentStepCompleted && (
          <div className="mt-4">
            {currentStepData.interaction_type === 'click' && (
              <Button
                onClick={() => handleStepInteraction(currentStep)}
                className="bg-teal-500 hover:bg-teal-600 text-white"
              >
                {currentStepData.interaction_data?.button_text || 'Continue'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
            
            {currentStepData.interaction_type === 'explore' && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  {currentStepData.interaction_data?.instruction || 'Click to explore:'}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {(currentStepData.interaction_data?.elements || []).map((element, idx) => (
                    <Button
                      key={idx}
                      onClick={() => handleStepInteraction(currentStep)}
                      className="bg-teal-500 hover:bg-teal-600 text-white h-auto py-4"
                    >
                      {element.label || element}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step completed feedback */}
        {isCurrentStepCompleted && (
          <div className="mt-4 bg-green-100 dark:bg-green-900/30 border-2 border-green-500 rounded-lg p-4">
            <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
              <Check className="w-5 h-5" />
              <span className="font-semibold">Step completed!</span>
            </div>
            {currentStepData.interaction_data?.feedback && (
              <p className="text-sm mt-2 text-green-700 dark:text-green-300">
                {currentStepData.interaction_data.feedback}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Navigation buttons */}
      <div className="flex justify-between">
        <Button
          onClick={handlePrevious}
          disabled={currentStep === 0}
          variant="outline"
        >
          Previous
        </Button>
        {currentStep < steps.length - 1 && (
          <Button
            onClick={handleNext}
            disabled={!isCurrentStepCompleted && currentStepData.interaction_type !== null}
            className="bg-teal-500 hover:bg-teal-600 text-white"
          >
            Next Step
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>

      {/* Completion message */}
      {allStepsCompleted && (
        <div className="bg-green-100 dark:bg-green-900/30 border-2 border-green-500 rounded-lg p-6 text-center">
          <Check className="w-12 h-12 text-green-600 mx-auto mb-3" />
          <p className="text-green-800 dark:text-green-200 font-semibold text-lg">
            {completionMessage}
          </p>
        </div>
      )}
    </div>
  );
}


